import { useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, RefreshCw, Search } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { adminApi } from "@/lib/admin-api";
import { fmtDate, useAdminList } from "./_useAdminList";

export default function AdminAudit() {
  const [actor, setActor] = useState("");
  const list = useAdminList({
    fetcher: adminApi.audit,
    dataKey: "auditLogs",
    defaultPageSize: 50,
  });
  const totalPages = Math.max(1, Math.ceil(list.total / list.pageSize));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">Audit</h1>
          <p className="text-sm text-muted-foreground">
            Admin support actions and security audit events. {list.total.toLocaleString()} total.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => list.refetch()} disabled={list.refreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${list.refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") list.setFilters({ actor });
            }}
            placeholder="Actor email or ID…"
            className="pl-9 h-9"
          />
        </div>
        <Button size="sm" variant="secondary" onClick={() => list.setFilters({ actor })}>
          Search
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {list.loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading audit logs…
            </div>
          ) : list.error ? (
            <div className="p-6 text-sm text-destructive">{list.error}</div>
          ) : list.data.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No audit logs found.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>At</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-xs">{fmtDate(log.at)}</TableCell>
                    <TableCell>{log.actorEmail || log.actorId || "system"}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {log.targetType}:{log.targetId || "—"}
                    </TableCell>
                    <TableCell><Badge variant="secondary">{log.action}</Badge></TableCell>
                    <TableCell className="max-w-md truncate">{log.reason || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Pager list={list} totalPages={totalPages} />
    </div>
  );
}

function Pager({ list, totalPages }) {
  return (
    <div className="flex items-center justify-between text-xs text-muted-foreground">
      <div>Page {list.page} of {totalPages} · {list.total.toLocaleString()} audit logs</div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={list.page <= 1} onClick={() => list.setPage(list.page - 1)}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Prev
        </Button>
        <Button size="sm" variant="outline" disabled={list.page >= totalPages} onClick={() => list.setPage(list.page + 1)}>
          Next <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
